import './styles/CompanyDetailPage.scss';

import { Edit, Add } from '@mui/icons-material';
import { Grid, Paper, Divider, Box, Typography, Skeleton } from '@mui/material';
import Alert from '@mui/material/Alert';

import parse from 'html-react-parser';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import {
  useJobmarketCompanyQuery,
  useJobmarketUniversityCompanyQuery
} from 'api/hooks/jobmarket/companies';
import { FormWrapperModal } from 'app/components/FormWrapperModal';
import { JobOfferItem } from 'app/components/RichList/items/JobOfferItem';
import { SubtleActionButton } from 'app/components/SubtleActionButton';
import { WatchlistAddButton } from 'app/components/WatchlistAddButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { Company } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { countries } from 'utils/Countries';
import Roles from 'utils/enums/Roles';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';
import usePageHeading from 'utils/hooks/usePageHeading';

import { CompanyDetail } from '../CompanyDetail';
import { CompanyDetailContact } from '../CompanyDetail/components/CompanyDetailContact';
import { ManageOffer } from '../ManageOffer';
import NoOffer from './components/NoOffer';
import OfferWarningModal from './components/OfferWarning';

export default function CompanyDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentURL = useCurrentURL();
  const dispatch = useDispatch();
  const { user: currentUser } = useAuthContext();
  const { id } = useParams<{ id: string }>();

  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [createOfferModalOpen, setCreateOfferModalOpen] = useState(false);
  const [createOfferModalWarningOpen, setCreateOfferModalWarningOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  const aclHelper = new ACLHelper(currentUser);

  usePageHeading({
    heading: t(translations.common.company()),
    headingProps: {
      back: location.state?.back
        ? location.state.back
        : aclHelper.isCompanyUser()
        ? `/`
        : '/companies'
    }
  });

  const {
    data: myCompany,
    isError,
    isLoading
  } = useJobmarketCompanyQuery(id ? parseInt(id) : undefined, {
    params: {
      withExpired: aclHelper.isStudent(true) ? undefined : true,
      withInactiveOffers: aclHelper.isStudent(true) ? undefined : true
    }
  });
  const {
    data: universityCompany,
    isError: isUniversityCompanyError,
    isLoading: isUniversityCompanyLoading
  } = useJobmarketUniversityCompanyQuery(undefined);

  useEffect(() => {
    if (aclHelper.isCompanyUser() && !aclHelper.isCompanyUser(id)) {
      dispatch(AlertActions.error(`${t(translations.pages.companyDetailPage.forbidden())}`));
      navigate(`/companies/${currentUser.company.id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shouldShowWarning = (companyData: Company) => {
    let emptyFields: string[] = [];
    if (
      !companyData.country ||
      countries.filter(country => country.label === companyData.country).length === 0
    ) {
      emptyFields.push(t(translations.pages.companyDetailPage.missingFields.country()));
    }
    if (!companyData.invoiceEmail) {
      emptyFields.push(t(translations.pages.companyDetailPage.missingFields.invoiceEmail()));
    }
    if (!companyData.taxId && !companyData.taxNumber) {
      emptyFields.push(t(translations.pages.companyDetailPage.missingFields.taxIdAndNumber()));
    }
    setMissingFields(emptyFields);
    if (emptyFields.length > 0) {
      return setCreateOfferModalWarningOpen(true);
    }
    return setCreateOfferModalOpen(true);
  };
  // TODO check if mainContactView Variable is still needed
  const userCanEdit =
    aclHelper.isCompanyUser(id) || aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN);
  const mainContactView =
    aclHelper.isCompanyUser(id) || aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN);
  const invoiceView =
    aclHelper.isCompanyUser() || aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN);

  const renderOffers = (company: Company) => {
    if (company.jobOfferings?.length === 0) {
      return <NoOffer />;
    }

    if (aclHelper.isCompanyUser() || aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)) {
      const openOffers = company.jobOfferings?.filter(o => o.isActive);
      const pastOffers = company.jobOfferings?.filter(o => !o.isActive);
      return (
        <Grid item xs>
          <Grid container paddingBottom={1}>
            {openOffers?.map(offer => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                className="joboffer"
                onClick={() => navigate(`/jobs/${offer.id}`, { state: { back: currentURL } })}
                key={offer.id}
              >
                <JobOfferItem
                  key={offer.id}
                  jobOffer={offer}
                  withId={aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)}
                  dense
                />
              </Grid>
            ))}
          </Grid>
          {pastOffers.length > 0 && <Divider variant="fullWidth" />}
          <Grid container>
            {pastOffers?.map(offer => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                className="joboffer"
                onClick={() => navigate(`/jobs/${offer.id}`, { state: { back: currentURL } })}
                key={offer.id}
              >
                <JobOfferItem
                  key={offer.id}
                  jobOffer={offer}
                  withId={aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)}
                  dense
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container>
        {company.jobOfferings?.map(offer => (
          <Grid
            item
            xs={12}
            md={6}
            lg={4}
            className="joboffer"
            onClick={() => navigate(`/jobs/${offer.id}`, { state: { back: currentURL } })}
            key={offer.id}
          >
            <JobOfferItem
              key={offer.id}
              jobOffer={offer}
              withId={aclHelper.hasOne('entitlement', Roles.JOBMARKET_ADMIN)}
              dense
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  if (isError || isUniversityCompanyError) {
    dispatch(AlertActions.error(`${t(translations.pages.companyDetailPage.openFailed())}`));
    navigate('/companies', { replace: true });
    return;
  }

  const company = (myCompany || universityCompany) as Company;

  if (isLoading || isUniversityCompanyLoading) {
    return (
      <Grid container className="company-detail-page">
        <Grid item xs={12} md={3}>
          {/* Contact section skeleton */}
          <Paper square sx={{ backgroundColor: 'transparent' }}>
            <Box p={2}>
              <Skeleton variant="text" width="60%" height={300} sx={{ justifySelf: 'center' }} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="70%" />
              <Box mt={2}>
                <Skeleton variant="text" width="50%" height={30} />
                <Skeleton variant="text" width="90%" />
                <Skeleton variant="text" width="85%" />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper square>
            {/* Company info section skeleton */}
            <Box p={2}>
              <Skeleton variant="text" width="40%" height={50} />
              <Box mt={2}>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="90%" />
              </Box>
            </Box>
            <Divider />
            {/* Job offerings section skeleton */}
            <Box p={2}>
              <Skeleton variant="text" width="30%" height={40} />
              <Grid container spacing={2} mt={2}>
                {[1, 2, 3].map(i => (
                  <Grid item xs={12} md={6} lg={4} key={i}>
                    <Skeleton variant="rectangular" height={200} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  if (company.state !== 'active' && !userCanEdit) {
    dispatch(AlertActions.error(`${t(translations.pages.companyDetailPage.notAvailable())}`));
    navigate('/', { replace: true });
  }

  return (
    <>
      <Helmet>
        <title>{company.name}</title>
      </Helmet>
      {userCanEdit && (
        <>
          <FormWrapperModal
            className="company-detail-modal"
            open={detailViewOpen}
            onClose={(props, reason) => {
              if (reason && reason === 'backdropClick') return;
              setDetailViewOpen(false);
            }}
          >
            <div className="company-detail-modal__body">
              <CompanyDetail
                companyId={company.id}
                closeHandler={() => {
                  setDetailViewOpen(false);
                  dispatch(FormErrorActions.close());
                }}
                noAdmin
                onlyEdit
              />
            </div>
          </FormWrapperModal>
          <FormWrapperModal
            open={createOfferModalWarningOpen}
            onClose={() => {
              setCreateOfferModalWarningOpen(false);
            }}
          >
            <OfferWarningModal
              setOpen={setCreateOfferModalWarningOpen}
              isOpen={createOfferModalWarningOpen}
              setEditCompanyOpen={setDetailViewOpen}
              setCreateOfferOpen={setCreateOfferModalOpen}
              emptyFields={missingFields}
            />
          </FormWrapperModal>
          <FormWrapperModal
            className="job-offer-detail-modal"
            open={createOfferModalOpen}
            onClose={(props, reason) => {
              if (reason && reason === 'backdropClick') return;
              setCreateOfferModalOpen(false);
            }}
          >
            <div className="job-offer-detail-modal__body">
              <ManageOffer
                company={company}
                newOffer
                closeHandler={() => setCreateOfferModalOpen(false)}
              />
            </div>
          </FormWrapperModal>
        </>
      )}
      <Grid container className="company-detail-page">
        {company.state !== 'active' && userCanEdit && (
          <Grid item xs={12} className="company-detail-page__alert">
            <Alert severity="info">{t(translations.pages.companyDetailPage.notActive())}</Alert>
          </Grid>
        )}
        {userCanEdit && company.state === 'active' && (
          <Grid container justifyContent="flex-end" className="company-detail-page__controls">
            <SubtleActionButton type="default" icon={Edit} onClick={() => setDetailViewOpen(true)}>
              {t(translations.common.edit())}
            </SubtleActionButton>
          </Grid>
        )}
        <CompanyDetailContact
          company={company}
          mainContactView={mainContactView}
          invoiceView={invoiceView}
        />
        <Grid component={Paper} square item xs={12} md={9} className="company-detail-page__company">
          <Grid container className="company-detail-page__company__section">
            <Grid item xs>
              <Grid container justifyContent={'space-between'}>
                <Typography className="company-detail-page__company__content__title">
                  {company.name}
                </Typography>
                <WatchlistAddButton
                  categoryName="COMPANY"
                  description={company.description}
                  title={company.name}
                  date={new Date()}
                  withoutDateFilter
                />
              </Grid>
              <div className="company-detail-page__company__content__description">
                <Box className="ql-editor">{parse(company.description)}</Box>
              </div>
            </Grid>
          </Grid>
          <Divider />
          <Grid container className="company-detail-page__company__section">
            <Grid
              container
              className="company-detail-page__company__section__header"
              justifyContent="space-between"
            >
              {t(translations.pages.jobOffers.jobOffers())} ({company.jobOfferings?.length}
              {company.jobOfferings?.length >= 50 ? '+' : ''})
              <Grid item xs="auto">
                {company.state === 'active' && userCanEdit && (
                  <Grid container justifyContent="flex-end">
                    <SubtleActionButton
                      type="primary"
                      icon={Add}
                      onClick={() => shouldShowWarning(company)}
                    >
                      {t(translations.pages.manageJobOffers.createNew())}
                    </SubtleActionButton>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {renderOffers(company)}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}
